import { getAuth } from '@firebase/auth';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useNavigation } from 'src/contexts/NavigationContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { logoutHelper } from 'src/helpers/logoutHelper';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { Voice } from 'src/interfaces/voice.interface';

const DEFAULT_AGENT_FEMALE_VOICE = {
  type: 'voice_eleven_labs',
  voice_id: 'cgSgspJ2msm6clMCkdW9',
  stability: 0.4,
  similarity_boost: 1.0,
  label: 'Jessica',
};

const BASE_AGENT_BODY: Partial<Agent> = {
  language: 'en',
  initial_message_delay: 0,
  max_idle_check_count: 3,
  idle_time_seconds: 7,
  call_duration_sec: 600,
  conversation_speed: 1.2,
  ask_if_human_present_on_idle: true,
  llm_temperature: 0,
  provider: 'openai',
  voice: DEFAULT_AGENT_FEMALE_VOICE as Voice,
  transcriber: {
    type: 'assemblyai',
  },
};

interface ChatAgentContextData {
  agent: Partial<Agent>;
  setAgent: Dispatch<SetStateAction<Partial<Agent>>>;
  agentLoading: boolean;
  saveAgent: (updatedAgent: Partial<Agent>) => Promise<void>;
  isValidAgent: () => boolean;
}

const ChatAgentContext = createContext<ChatAgentContextData>(
  {} as ChatAgentContextData,
);

interface ChatAgentProviderProps {
  children: ReactNode;
}

export function ChatAgentProvider({ children }: ChatAgentProviderProps) {
  const { get, post } = useAuthedRequest();
  const auth = getAuth();
  const { setBlocked } = useNavigation();
  const notification = useNotification();

  const [agent, setAgent] = useState<Partial<Agent>>(BASE_AGENT_BODY);
  const [loading, setLoading] = useState(false);

  const saveAgent = useCallback(
    async (updatedAgent: Partial<Agent>) => {
      setLoading(true);
      const url = '/agents';

      if (!(await canCreateAgent())) {
        setLoading(false);
        return;
      }

      const hasPostCallPrompt = !!updatedAgent.post_call_prompt?.trim();
      const agentToCreate = {
        ...updatedAgent,
        ...(hasPostCallPrompt
          ? { post_call_prompt: updatedAgent.post_call_prompt }
          : {
              post_call_insights: updatedAgent.post_call_insights ?? [],
            }),
        ...(hasPostCallPrompt && {
          post_call_insights: undefined,
        }),
        ...(!hasPostCallPrompt && {
          post_call_prompt: undefined,
        }),
      } as Partial<Agent>;

      const createdAgent = await post(url, agentToCreate);
      setAgent(createdAgent);
      setLoading(false);
      setBlocked(false);
    },
    [post],
  );

  const canCreateAgent = useCallback(async () => {
    const resp = await auth.currentUser?.getIdTokenResult();
    if (!resp) {
      notification.error(`Auth expired`);
      return logoutHelper();
    }
    const { id } = resp.claims;

    const plan = await get(`/users/${id}/plan`);
    const query = new URLSearchParams({ size: '1', page: '1' });
    const data = await get(`/agents?${query.toString()}`);

    if (plan) {
      const maxAgents = plan?.featureGates?.agent;
      if (maxAgents && data.total >= maxAgents) {
        notification.error(
          `The ${plan.name} plan is limited to ${maxAgents} agent(s). Please upgrade to add more agents`,
        );
        return false;
      }
    }
    return true;
  }, [auth, get, notification]);

  const isValidAgent = () => {
    return !!(
      agent.voice &&
      agent.prompt &&
      String(agent.prompt) !== '' &&
      (agent.prompt as Prompt).content !== ''
    );
  };

  return (
    <ChatAgentContext.Provider
      value={{
        agent,
        setAgent,
        agentLoading: loading,
        saveAgent,
        isValidAgent,
      }}
    >
      {children}
    </ChatAgentContext.Provider>
  );
}

export function useChatAgent() {
  const context = useContext(ChatAgentContext);

  if (!context) {
    throw new Error('useChatAgent must be used within an ChatAgentProvider');
  }

  return context;
}
